import * as _ from 'lodash';
import { getHackyWindowProps } from '../getHackyWindowProps';
import { createStoreFrontBILogger } from '../../bi/configure-front-bi-logger';
import { WixStoresHttp } from '../../http/http';
import { FetchAdapter, InstanceManager } from '../../http/fetch-wrapper';
import { CacheKey, CSRF_HEADER_NAME, MULTI_LANG_HEADER_NAME, Topology } from '../../constants';
import Experiments from '@wix/wix-experiments';
import { query as getConfigQuery } from '../../graphql/getConfig.graphql';
var SiteStore = /** @class */ (function () {
    function SiteStore(_a, _b, _c, platform) {
        var _d;
        var instance = _a.instance, instanceId = _a.instanceId, baseUrls = _a.baseUrls;
        var pubSub = _b.pubSub, storage = _b.storage;
        var wixWindow = _c.window, site = _c.site, location = _c.location;
        this.pageMap = {
            cart: 'shopping_cart',
            checkout: 'checkout',
            gallery: 'product_gallery',
            thankyou: 'thank_you_page',
            product: 'product_page',
        };
        this.instance = instance;
        this.formFactor = wixWindow.formFactor;
        this.viewMode = wixWindow.viewMode;
        this.pubSub = pubSub;
        this.siteApis = site;
        this.location = location;
        this.windowApis = wixWindow;
        this.storeId = instanceId;
        this.msid = platform.bi.metaSiteId;
        this.platformServices = platform;
        this.storage = storage;
        var isMerchant = getHackyWindowProps().clientConfig.isMerchant;
        var user = isMerchant ? { uuid: platform.bi.ownerId } : { visitor_id: platform.bi.visitorId };
        this.biLogger = createStoreFrontBILogger(user, platform.bi.biToken, {
            storeId: this.storeId,
            isMerchant: isMerchant,
            appName: 'Stores',
            _msid: this.msid,
        });
        this.httpClient = new WixStoresHttp(new InstanceManager(this.instance), new FetchAdapter());
        var multiLangHeader = this.getMultiLangHeader();
        if (multiLangHeader) {
            this.httpClient.addHeaders((_d = {}, _d[MULTI_LANG_HEADER_NAME] = multiLangHeader, _d));
        }
        if (!this.location.navigateToSection) {
            this.location.navigateToSection = function () { return Promise.resolve(); };
        }
        var overrideBaseUrl = _.has(location.query, 'localDevServer');
        this.baseUrls = _.mapValues(baseUrls, function (url) { return (overrideBaseUrl ? location.query.localDevServer : url); });
        this.isSiteMode() && this.purgeMemoryCache();
    }
    SiteStore.prototype.loadClientConfig = function () {
        var _this = this;
        var cache = this.storage.memory.getItem(CacheKey.GET_CONFIG);
        if (cache) {
            this.handleGetConfigData(JSON.parse(cache));
            this.addCSRFToken();
            return Promise.resolve();
        }
        return this.httpClient
            .post(this.resolveAbsoluteUrl("/" + Topology.STOREFRONT_GRAPHQL_URL), {
            query: getConfigQuery,
            source: 'WixStoresWebClient',
            operationName: 'getConfig',
        })
            .then(function (_a) {
            var data = _a.data;
            _this.isSiteMode() && _this.storage.memory.setItem(CacheKey.GET_CONFIG, JSON.stringify(data));
            _this.handleGetConfigData(data);
        })
            .then(function () {
            _this.addCSRFToken();
        });
    };
    SiteStore.prototype.isMobile = function () {
        return this.formFactor === 'Mobile';
    };
    SiteStore.prototype.isDesktop = function () {
        return this.formFactor === 'Desktop';
    };
    SiteStore.prototype.isTablet = function () {
        return this.formFactor === 'Tablet';
    };
    SiteStore.prototype.isEditorMode = function () {
        return this.viewMode === 'Editor';
    };
    SiteStore.prototype.isSiteMode = function () {
        return this.viewMode === 'Site';
    };
    SiteStore.prototype.isPreviewMode = function () {
        return this.viewMode === 'Preview';
    };
    SiteStore.prototype.getSectionUrl = function (sectionId) {
        return this.siteApis.getSectionUrl({ sectionId: sectionId });
    };
    SiteStore.prototype.getBaseUrl = function () {
        var baseUrl = this.location.baseUrl; //?
        var index = baseUrl.lastIndexOf('/');
        return baseUrl.substring(index, 0);
    };
    SiteStore.prototype.getOrigin = function () {
        var baseUrl = this.location.baseUrl;
        var baseUrlParts = baseUrl.split('/');
        return baseUrlParts.slice(0, 3).join('/');
    };
    SiteStore.prototype.resolveAbsoluteUrl = function (path) {
        return this.isSSR() ? "" + this.getOrigin() + path : path;
    };
    SiteStore.prototype.isInteractive = function () {
        return this.windowApis.rendering.env === 'browser';
    };
    SiteStore.prototype.isSSR = function () {
        return this.windowApis.rendering.env === 'backend';
    };
    SiteStore.prototype.isRTL = function () {
        return this.layoutDirection === 'rtl';
    };
    SiteStore.prototype.getMultiLangFields = function () {
        var currentShortLang = this.windowApis.multilingual.currentLanguage;
        var currentLang = this.windowApis.multilingual.siteLanguages.filter(function (lang) { return lang.languageCode === currentShortLang; });
        if (currentLang.length > 0) {
            return {
                isPrimaryLanguage: currentLang[0].isPrimaryLanguage,
                lang: currentShortLang,
                locale: currentLang[0].locale,
            };
        }
        return null;
    };
    SiteStore.prototype.getMultiLangHeader = function () {
        var fields = this.getMultiLangFields();
        return fields ? fields.lang + "|" + fields.locale + "|" + fields.isPrimaryLanguage.toString() + "|" + this.storeId : '';
    };
    SiteStore.prototype.addCSRFToken = function () {
        var _a;
        if (this.experiments.enabled('specs.stores.UseCsrfHeader')) {
            var csrf = this.platformServices.getCsrfToken();
            if (csrf) {
                this.httpClient.addHeaders((_a = {}, _a[CSRF_HEADER_NAME] = csrf, _a));
            }
        }
    };
    SiteStore.prototype.handleGetConfigData = function (data) {
        this.handleExperiments(data.experiments);
        this.locale = data.clientConfig.language;
        this.currency = data.clientConfig.storeCurrency;
        this.layoutDirection = data.clientConfig.layoutDirection;
    };
    SiteStore.prototype.handleExperiments = function (experimentsData) {
        var experiments = (experimentsData || []).reduce(function (acc, e) {
            acc[e.name] = e.value;
            return acc;
        }, {});
        this.experiments = new Experiments({ experiments: experiments });
    };
    SiteStore.prototype.purgeMemoryCache = function () {
        var cachedLang = this.storage.memory.getItem(CacheKey.MULTI_LANG);
        if (cachedLang !== this.windowApis.multilingual.currentLanguage) {
            this.storage.memory.clear();
            this.storage.memory.setItem(CacheKey.MULTI_LANG, this.windowApis.multilingual.currentLanguage);
        }
    };
    return SiteStore;
}());
export { SiteStore };
//# sourceMappingURL=siteStore.js.map